import { useState, useEffect, useMemo, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { bookingApi } from '../services/bookingService';
import { getErrorMessage } from '../utils/errors';
import { todayInRiyadh, formatDateToIso } from '../utils/availability';
import { BOOKING_TIMEZONE, type AvailabilitySlot, type BookingCreateResponse, type CalendarProvider, type ConsultationService } from '../types';


export interface BookingFormState {
  customerName: string;
  email: string;
  phone: string;
  company: string;
  selectedDate: string;
  selectedSlot: AvailabilitySlot | null;
  provider: CalendarProvider;
  notes: string;
}

function createInitialState(): BookingFormState {
  return {
    customerName: '',
    email: '',
    phone: '',
    company: '',
    selectedDate: '',
    selectedSlot: null,
    provider: 'GOOGLE',
    notes: '',
  };
}

export function useBookingAvailability(service: ConsultationService | null, isOpen: boolean) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BookingFormState>(createInitialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<BookingCreateResponse | null>(null);
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [fullDays, setFullDays] = useState<Set<string>>(new Set());
  const [loadingDays, setLoadingDays] = useState(false);

  const resetBookingState = useCallback(() => {
    setForm(createInitialState());
    setSubmitting(false);
    setSubmitError('');
    setResult(null);
    setSlots([]);
    setSlotsError('');
    setFullDays(new Set());
    setAvailableDates(new Set());
  }, []);

  // Sync initial state when modal opens/closes
  useEffect(() => {
    if (isOpen && service) {
      resetBookingState();
    }
  }, [isOpen, service, resetBookingState]);

  // Load available dates from backend in a single call
  useEffect(() => {
    if (!isOpen || !service) return;

    let cancelled = false;
    setLoadingDays(true);

    bookingApi.getAvailableDates()
      .then((configs) => {
        if (cancelled) return;
        const dates = configs.map((c) => c.date);
        const dateSet = new Set(dates);
        setAvailableDates(dateSet);

        // Auto-select the first available date
        if (dates.length > 0) {
          const today = todayInRiyadh();
          const firstFuture = dates.find((d) => d > today);
          if (firstFuture) {
            setForm((current) => ({ ...current, selectedDate: firstFuture, selectedSlot: null }));
          }
        }
      })
      .catch(() => {
        /* silently handle — calendar will show no dates */
      })
      .finally(() => {
        if (!cancelled) setLoadingDays(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, service]);

  // Load slots for selected date
  useEffect(() => {
    if (!isOpen || !service || !form.selectedDate) {
      setSlots([]);
      return;
    }

    let cancelled = false;
    setLoadingSlots(true);
    setSlotsError('');

    bookingApi.getAvailability(form.selectedDate, service.durationMinutes)
      .then((data) => {
        if (cancelled) return;
        setSlots(data.slots);

        // Mark the day as full if no slots are available
        const anyAvailable = data.slots.some((s) => !s.booked);
        if (!data.available || !anyAvailable) {
          setFullDays((current) => {
            const next = new Set(current);
            next.add(form.selectedDate);
            return next;
          });
        }

        setForm((current) => {
          if (current.selectedSlot) {
            const stillValid = data.slots.some(
              (s) => s.startAt === current.selectedSlot?.startAt && !s.booked,
            );
            if (stillValid) return current;
          }
          const firstAvailable = data.slots.find((s) => !s.booked) ?? null;
          return { ...current, selectedSlot: firstAvailable };
        });
      })
      .catch(async (loadError) => {
        if (cancelled) return;
        const message = await getErrorMessage(loadError, t('booking.slotsLoadFailed'));
        setSlotsError(message);
        toast.error(message);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, service, form.selectedDate, t]);

  const disabledMatcher = useMemo(() => {
    const today = todayInRiyadh();

    return (date: Date) => {
      const iso = formatDateToIso(date);
      // Disable if: today or past, OR not in admin's available dates, OR fully booked
      if (iso <= today) return true;
      if (!availableDates.has(iso)) return true;
      if (fullDays.has(iso)) return true;
      return false;
    };
  }, [availableDates, fullDays]);

  const handleDateSelect = useCallback((date: Date | undefined) => {
    if (!date) return;
    const iso = formatDateToIso(date);
    if (disabledMatcher(date)) return;
    setForm((current) => ({ ...current, selectedDate: iso, selectedSlot: null }));
  }, [disabledMatcher]);

  const handleSlotPick = useCallback((slot: AvailabilitySlot) => {
    setForm((current) => ({ ...current, selectedSlot: slot }));
  }, []);

  const handleChange = useCallback((field: keyof BookingFormState) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  }, []);

  const submitBooking = useCallback(async (serviceId: string, serviceName: string, locale: string) => {
    if (!form.selectedSlot) {
      toast.warn(t('booking.invalidDate'));
      return false;
    }
    setSubmitting(true);
    setSubmitError('');

    try {
      const data = await bookingApi.createBooking({
        serviceId,
        serviceName,
        customerName: form.customerName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim() || undefined,
        startAt: form.selectedSlot.startAt,
        endAt: form.selectedSlot.endAt,
        timezone: BOOKING_TIMEZONE,
        calendarProvider: form.provider,
        locale,
        notes: form.notes.trim() || undefined,
      });

      setResult(data);
      toast.success(t('booking.successTitle'));
      setSubmitting(false);
      return true;
    } catch (submitErrorInner) {
      const message = await getErrorMessage(submitErrorInner, t('booking.submitFailed'));
      setSubmitError(message);
      toast.error(message);
      setSubmitting(false);
      return false;
    }
  }, [form, t]);

  return {
    form,
    slots,
    loadingSlots,
    slotsError,
    fullDays,
    loadingDays,
    submitting,
    submitError,
    result,
    disabledMatcher,
    handleDateSelect,
    handleSlotPick,
    handleChange,
    submitBooking,
    resetBookingState,
  };
}
